import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import 'components/charts/chart.css';
import classNames from 'classnames';
import { ShowHide } from 'components/layout';
import { NonIdealState } from '@blueprintjs/core';
import { CHART_COLORS } from 'components/charts';

export function StackedBarChart({
    title,
    items,
    style,
    loading = false,
    noDataMessage = "No data to display",
    onItemClick = null
}) {

    const total = useMemo(() => {
        return items.reduce((acc, item) => acc + item.value, 0);
    }, [items]);

    const getItemPercentage = (value) => {
        return ((value / total) * 100).toFixed(2);
    }

    const getColourByIndex = (index) => {
        return CHART_COLORS[index % CHART_COLORS.length];
    }

    return (
        <div className="chart-container" style={style}>
            <span className='chart-title'>CHART</span>
            <h3 className='spacer-bottom'>{title}</h3>
            <ShowHide
                evaluator={loading || items?.length}
                show={(
                    <>
                        <div className={classNames("stacked-bar-chart-container", {'bp6-skeleton': loading })}>
                            <div className='stacked-bar-chart-items'>
                                {items.map((item, index) => {
                                    const segmentClassName = classNames('stacked-bar-chart-item-container', { clickable: onItemClick != null });
                                    const segmentStyle = {
                                        background: item.color ?? getColourByIndex(index),
                                        flexBasis: `${getItemPercentage(item.value)}%`
                                    };
                                    if (onItemClick != null) {
                                        return (
                                            <button
                                                type="button"
                                                className={segmentClassName}
                                                key={item.id}
                                                style={segmentStyle}
                                                onClick={() => onItemClick(item)}
                                            />
                                        );
                                    }
                                    return (
                                        <div
                                            className={segmentClassName}
                                            key={item.id}
                                            style={segmentStyle}
                                        />
                                    );
                                })}
                            </div>
                            <div className="stacked-bar-chart-labels stacked-bar-chart-title spacer-top-small">
                                {items.map((item, index) => (
                                    <div key={item.id} className="stacked-bar-chart-labels-item" style={{
                                        flexBasis: `${getItemPercentage(item.value)}%`
                                    }}>
                                        <span>
                                            {item.onRender?.(item.value, index) ?? item.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='stacked-bar-chart-legend'>
                            {items.map((item, index) => {
                                const legendClassName = classNames('stacked-bar-chart-legend-item', { clickable: onItemClick != null });
                                const legendChildren = (
                                    <>
                                        <span className='stacked-bar-chart-title'>
                                            {item.label}
                                        </span>
                                        <span className='stacked-bar-chart-legend-value' style={{ color: item.color ?? getColourByIndex(index) }}>
                                            {item.onRender?.(item.value, index) ?? item.value}
                                        </span>
                                    </>
                                );
                                if (onItemClick != null) {
                                    return (
                                        <button
                                            type="button"
                                            className={legendClassName}
                                            key={item.id}
                                            onClick={() => onItemClick(item)}
                                        >
                                            {legendChildren}
                                        </button>
                                    );
                                }
                                return (
                                    <div className={legendClassName} key={item.id}>
                                        {legendChildren}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
                hide={<NonIdealState
                    title={''}
                    className='spacer-bottom-small'
                    description={noDataMessage}
                />}
            />

        </div>
    );
}

StackedBarChart.propTypes = {
    title: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
            color: PropTypes.string,
            onRender: PropTypes.func
        })
    ).isRequired,
    style: PropTypes.object,
    loading: PropTypes.bool,
    noDataMessage: PropTypes.string,
    onItemClick: PropTypes.func
};